import AuthServiceRepository from "../usecases/ports/authServiceRepository";
import {
  auth, 
  db, 
  setPersistence, 
  browserLocalPersistence, 
  doc, 
  getDocs, 
  updateDoc, 
  query, 
  collection, 
  where,
  signInWithEmailAndPassword,
  confirmPasswordReset,
  reauthenticateWithCredential,
  onAuthStateChanged,
  EmailAuthProvider,
  signOut,
} from "../../firebase";
import axios from "../../axios";
import { data as constants } from "../../constants";

const addFailedAttempt = async (docParam: any, collectionName: string) => {
  let failedLoginAttempts = docParam.failedLoginAttempts;
  let accountStatus = docParam?.accountStatus || 'active';

  const collectionRef = doc(db, collectionName, docParam.id);
  await updateDoc(collectionRef, {
    failedLoginAttempts: !failedLoginAttempts
      ? 1
      : (failedLoginAttempts += 1),
    accountStatus: failedLoginAttempts >= 5 ? "suspended" : accountStatus
  });
};

const userSuspended = async (collectionName: string, email: string) => {
  const q = query(collection(db, collectionName), where("email", "==", email));
  const data = await getDocs(q);

  let failedLoginAttempts = data.docs[0].data().failedLoginAttempts;
  let accountStatus = data.docs[0].data().accountStatus;
  if (accountStatus === "suspended" || failedLoginAttempts > 4) {
    return { success: false };
  }

  const collectionRef = doc(db, collectionName, data.docs[0].data().id);
  await updateDoc(collectionRef, {
    failedLoginAttempts: 0
  });

  return { success: true };
};

const fetchUser = async (email: string, collectionName: string) => {
  try {
    const q = query(collection(db, collectionName), where("email", "==", email));
    const userData = await getDocs(q);

    if (userData.docs.length) {
      return userData.docs[0].data();
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export default class AuthServiceFirebaseImpl implements AuthServiceRepository {
  login = async (
    email: string,
    password: string
  ): Promise<{
    user: {
      token: string;
      id: string;
      isAdmin: boolean;
      isSuperAdmin: boolean;
      name: string;
      email: string;
    } | null;
  }> =>
    setPersistence(auth, browserLocalPersistence)
      .then(async () => {
        try {
          const userCred = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );

          if (userCred.user) {
            const tokenResult = await userCred.user.getIdTokenResult();
            const res = await userSuspended('user', email);

            if (!res.success) {
              throw new Error(constants.loginErrorMessages.suspendedAccount);
            }

            return {
              user: {
                token: tokenResult.token,
                id: userCred.user.uid,
                isAdmin: tokenResult.claims?.isAdmin || false,
                isSuperAdmin: tokenResult.claims?.isSuperAdmin || false,
                name: tokenResult.claims?.name,
                email: tokenResult.claims?.email
              }
            };
          }
          return { user: null };
        } catch (err: any) {
          const errorMessage = err.message;
          if (
            errorMessage.includes(
              'wrong-password'
            )
          ) {
            const userData = await fetchUser(email, "user");
            addFailedAttempt(userData, "user");
            if (
              userData?.accountStatus === "suspended" ||
              userData?.failedLoginAttempts >= 4
            ) {
              throw new Error(constants.loginErrorMessages.suspendedAccount);
            }
          }
          throw err;
        }
      });

  logout = async (): Promise<boolean> => {
    try {
      await signOut(auth);
      return true;
    } catch (err) {
      return false;
    }
  };

  sendResetPasswordEmail = async (email: string): Promise<void> => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      await axios.post(`/user/send-reset-password`, { email });
    } catch (error) {
      throw error;
    }
  };

  confirmPasswordReset = async (
    code: string,
    newPassword: string
  ): Promise<void> => {
    try {
      await confirmPasswordReset(auth, code, newPassword);
    } catch (error) {
      throw error;
    }
  };

  reauthenticateUser = async (password: string): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (user) {
        const credential = EmailAuthProvider.credential(
          user?.email || "",
          password
        );
        await reauthenticateWithCredential(user, credential);
      }
    } catch (error) {
      throw error;
    }
  };

  verifyAuth = async (): Promise<boolean> => {
    try {
      return await new Promise((resolve, reject) => {
        const subscribe = onAuthStateChanged(auth, user => {
          subscribe();
          resolve(user);
        }, reject);
      }).then(async (user: any) => {
        await auth.currentUser?.reload();
        if (user) {
          return true;
        } else {
          return false;
        }
      });
    } catch (error) {
      throw error;
    }
  };
}
